// hh-be3 (case-34) — EditRoomScreen.
//
// Rename / delete a room. Reached from the property tree (the pencil button
// on a room header). Mirrors EditItemScreen: there is no GET
// /api/v1/rooms/{id}, so the room is located by walking the property tree
// (GET /api/v1/properties/tree). Backend:
//   - PATCH  /api/v1/rooms/{id}  — name
//   - DELETE /api/v1/rooms/{id}  — items/equipment re-attach to the property
// Delete is behind a window.confirm; if the id is absent from the tree the
// screen shows a "not found" state.

import type { JSX } from 'preact';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { T, Btn, resolveIconName } from '../../tokens';
import { ScreenHeader } from '../../shell';
import type { RouterApi } from '../../router';
import { rooms, humanizeError } from '../../api';
import type { PropertyTreeNode } from '../../api/endpoints/properties.js';
import { usePropertyTree } from './usePropertyTree';
import { IconPicker } from './IconPicker';
import { FormLabel, FormInput, FormFooter, FormError } from './formPrimitives';

export interface EditRoomScreenProps {
  readonly router: RouterApi;
  readonly roomId: string;
}

interface LocatedRoom {
  readonly name: string;
  /** Текущее значение icon: имя иконки (ручной выбор) или null (авто). */
  readonly icon: string | null;
}

/** Walks the tree to find the room across every property's `rooms`. */
function locateRoom(
  tree: readonly PropertyTreeNode[],
  roomId: string,
): LocatedRoom | null {
  for (const property of tree) {
    for (const room of property.rooms) {
      if (room.id === roomId) {
        return { name: room.name, icon: room.icon ?? null };
      }
    }
  }
  return null;
}

function describeError(err: unknown): string {
  return humanizeError(err, 'save');
}

export function EditRoomScreen({ router, roomId }: EditRoomScreenProps): JSX.Element {
  const tree = usePropertyTree();
  const [name, setName] = useState<string>('');
  // null = авто-подбор иконки; строка = ручной выбор.
  const [icon, setIcon] = useState<string | null>(null);
  const [original, setOriginal] = useState<LocatedRoom | null>(null);
  const [hydrated, setHydrated] = useState<boolean>(false);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Hydrate the form once from the tree.
  useEffect(() => {
    if (tree.status !== 'ready' || hydrated) return;
    const found = locateRoom(tree.properties, roomId);
    if (found !== null) {
      setName(found.name);
      setIcon(found.icon);
      setOriginal(found);
    }
    setHydrated(true);
  }, [tree.status, tree.properties, roomId, hydrated]);

  const notFound = hydrated && original === null;

  const autoIcon = useMemo(() => resolveIconName('room', name), [name]);

  const canSave = useMemo<boolean>(
    () => original !== null && name.trim().length > 0 && !pending,
    [original, name, pending],
  );

  const back = (): void => router.back();

  const save = (): void => {
    if (original === null || name.trim().length === 0) return;
    const trimmed = name.trim();
    const nameChanged = trimmed !== original.name;
    const iconChanged = icon !== original.icon;
    if (!nameChanged && !iconChanged) {
      router.back();
      return;
    }
    setPending(true);
    setError(null);
    // PATCH аддитивный: шлём только изменившиеся поля. icon: строка → ручная
    // установка, null → сброс на авто (контракт hh-345).
    const patch: { name?: string; icon?: string | null } = {};
    if (nameChanged) patch.name = trimmed;
    if (iconChanged) patch.icon = icon;
    rooms
      .updateRoom(roomId, patch)
      .then(() => {
        router.back();
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  const remove = (): void => {
    if (pending) return;
    const ok = window.confirm(
      `Удалить комнату «${original?.name ?? 'комнату'}»? Вещи и оборудование переедут в собственность.`,
    );
    if (!ok) return;
    setPending(true);
    setError(null);
    rooms
      .deleteRoom(roomId)
      .then(() => {
        router.go('tree');
      })
      .catch((err: unknown) => {
        setError(describeError(err));
        setPending(false);
      });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg }}>
      <ScreenHeader title="Править комнату" subtitle="Вещи и история сохранятся" onBack={back} />
      <div style={{ flex: 1, overflow: 'auto', padding: '4px 16px 16px' }}>
        {tree.status === 'loading' && (
          <div style={{ fontSize: 12.5, color: T.ink3, padding: '6px 2px' }}>Загружаю…</div>
        )}
        {tree.status === 'error' && tree.error !== null && (
          <FormError message={tree.error} />
        )}
        {notFound && <FormError message="Комната не найдена — возможно, уже удалена." />}

        {tree.status === 'ready' && original !== null && (
          <>
            <FormLabel>Название</FormLabel>
            <FormInput value={name} onChange={setName} disabled={pending} />

            <div style={{ marginTop: 18 }}>
              <IconPicker
                value={icon}
                onChange={setIcon}
                autoIcon={autoIcon}
                disabled={pending}
              />
            </div>

            <Btn
              kind="danger"
              icon="trash"
              onClick={remove}
              disabled={pending}
              style={{ marginTop: 18, width: '100%' }}
            >
              Удалить комнату
            </Btn>

            {error !== null && <FormError message={error} />}
          </>
        )}
      </div>
      <FormFooter
        onCancel={back}
        onConfirm={save}
        confirmLabel="Сохранить"
        confirmIcon="check"
        disabled={!canSave}
        pending={pending}
      />
    </div>
  );
}
